import { ActionIcon, Button, Select, Text } from '@mantine/core';
import { Dropzone, type FileRejection, type FileWithPath } from '@mantine/dropzone';
import { FileCheck, Upload, X } from 'lucide-react';
import { type MouseEvent, useState } from 'react';

import { ACCEPTED_MIME_TYPES, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_LABEL } from '@/constants/file-upload.ts';

import { useFileUpload } from '@/hooks/use-file-upload.ts';

import { ModalType } from '@/models/Modal.ts';
import { DocumentType } from '@/models/documents/Document.ts';

import { closeModal } from '@/services/modalService.tsx';

import Paragraph from '../text/Paragraph.tsx';

const DOCUMENT_TYPE_OPTIONS = [
    { value: DocumentType.PRESCRIPTION, label: 'Rezept / Verordnung' },
    { value: DocumentType.TRANSFER_SLIP, label: 'Überweisung' },
    { value: DocumentType.FINDINGS, label: 'Befund' },
    { value: DocumentType.LAB_RESULTS, label: 'Labor' },
    { value: DocumentType.IMAGING, label: 'Bildgebende Verfahren (Röntgen, MRT, CT)' },
    { value: DocumentType.DOCTOR_LETTER, label: 'Arztbrief' },
    { value: DocumentType.MEDICATION_PLAN, label: 'Medikationsplan' },
    { value: DocumentType.OTHER, label: 'Sonstiges Dokument' },
];

const handleRemoveMouseDown = (event: MouseEvent): void => {
    event.stopPropagation();
};

interface Props {
    userId: string;
    appointmentId?: string;
    instanceId?: string;
    onUploadSuccess?: () => void;
}

export const FileUploadModalContent = ({ userId, appointmentId, instanceId, onUploadSuccess }: Props) => {
    const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
    const [dropzoneError, setDropzoneError] = useState<string | undefined>(undefined);
    const [documentType, setDocumentType] = useState<DocumentType>(DocumentType.OTHER);

    const handleUploadSuccess = (): void => {
        if (onUploadSuccess) {
            onUploadSuccess();
        }
        closeModal(ModalType.FILE_UPLOAD);
    };

    const { isUploading, validateFile, uploadFile } = useFileUpload({
        userId: userId,
        appointmentId: appointmentId,
        instanceId: instanceId,
        swrKey: ['appointment-documents', userId, appointmentId],
        onUploadSuccess: handleUploadSuccess,
    });

    const handleSubmit = async (): Promise<void> => {
        if (!selectedFile) return;
        await uploadFile(selectedFile, documentType);
    };

    const handleDocumentTypeChange = (value: string | null): void => {
        if (value) {
            setDocumentType(value as DocumentType);
        }
    };

    const handleDrop = (files: FileWithPath[]): void => {
        const file = files[0];
        if (file) {
            setSelectedFile(file);
            setDropzoneError(undefined);
        }
    };

    const handleReject = (rejections: FileRejection[]): void => {
        const rejection = rejections[0];
        if (!rejection) return;

        const error = validateFile(rejection.file);
        setDropzoneError(error ?? 'Die Datei konnte nicht verarbeitet werden.');
        setSelectedFile(undefined);
    };

    const handleRemoveFile = (event: MouseEvent): void => {
        event.stopPropagation();
        setSelectedFile(undefined);
        setDropzoneError(undefined);
    };

    const validationError = dropzoneError ?? (selectedFile ? validateFile(selectedFile) : undefined);

    return (
        <div className="flex flex-col gap-4">
            <Paragraph className="text-sm text-slate-500">
                Die Dokumente werden in der Patientenakte abgelegt.
            </Paragraph>

            <Select
                label="Dokumenttyp"
                data={DOCUMENT_TYPE_OPTIONS}
                value={documentType}
                onChange={handleDocumentTypeChange}
            />

            <Dropzone
                onDrop={handleDrop}
                onReject={handleReject}
                accept={ACCEPTED_MIME_TYPES}
                maxSize={MAX_FILE_SIZE_BYTES}
                multiple={false}
                loading={isUploading}
            >
                <div className="flex flex-col items-center justify-center gap-2 py-6">
                    <Dropzone.Accept>
                        <FileCheck size={40} className="text-green-600" />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                        <X size={40} className="text-red-600" />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                        <Upload size={40} className="text-slate-400" />
                    </Dropzone.Idle>

                    {selectedFile ? (
                        <div className="flex items-center gap-2">
                            <Text size="sm">{selectedFile.name}</Text>
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="sm"
                                onClick={handleRemoveFile}
                                onMouseDown={handleRemoveMouseDown}
                                aria-label="Datei entfernen"
                                style={{ pointerEvents: 'all' }}
                            >
                                <X size={16} />
                            </ActionIcon>
                        </div>
                    ) : (
                        <>
                            <Text size="sm" className="text-center">
                                Datei hierher ziehen oder klicken zum Auswählen
                            </Text>
                            <Text size="xs" className="text-center text-slate-400">
                                PDF, JPG, JPEG oder PNG – max. {MAX_FILE_SIZE_LABEL}
                            </Text>
                        </>
                    )}
                </div>
            </Dropzone>

            {validationError && (
                <Text size="sm" className="text-red-500">
                    {validationError}
                </Text>
            )}

            <Button
                onClick={handleSubmit}
                loading={isUploading}
                disabled={isUploading || !selectedFile || !!validationError}
                className="w-full"
            >
                Dokument hochladen
            </Button>
        </div>
    );
};
